import React, { useState, useMemo, useRef, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useParams } from 'react-router-dom';

import { axiosPrivate } from '../../../api/axios';
import { useSelector } from 'react-redux';

// Default / Initial Structure
const INITIAL_WIDGETS = Array.from({ length: 9 }, (_, i) => ({
  id: i + 1,
  title: `연동 대기 중 ${i + 1}`,
  value: '---',
  unit: '-',
  status: 'normal'
}));

const WidgetCard = ({ widget, onClick, isSelected }) => {
  if (!widget.metrics) return null;

  const numMetrics = widget.metrics.length;
  // Reduce value text size if metrics >= 4 OR if it's the selected floating widget in graph mode
  const isCompressed = numMetrics >= 4 || isSelected;

  const getStatusColor = (status) => {
    switch (status) {
      case 'critical': return 'bg-rose-500';
      case 'warning': return 'bg-amber-500';
      default: return 'bg-emerald-500';
    }
  };

  const getStatusShadow = (status) => {
    switch (status) {
      case 'critical': return 'rgba(244, 63, 94, 0.6)';
      case 'warning': return 'rgba(251, 191, 36, 0.6)';
      default: return 'rgba(16, 185, 129, 0.6)';
    }
  };

  return (
    <div
      onClick={() => onClick && onClick(widget)}
      style={{
        borderColor: isSelected ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.12)',
        backgroundColor: isSelected ? 'rgba(2, 6, 23, 0.7)' : 'rgba(15, 23, 42, 0.45)', // More transparent if selected
        boxShadow: isSelected ? '0 -10px 40px rgba(0,0,0,0.6)' : 'none',
        borderStyle: 'solid',
        borderWidth: isSelected ? '1px 0 0 0' : '2px',
        borderRadius: isSelected ? '0' : '12px',
        backdropFilter: isSelected ? 'blur(40px)' : 'blur(30px)'
      }}
      className={`
        relative cursor-pointer transition-all duration-500
        flex items-center group backdrop-blur-3xl
        w-full
        ${isSelected ? 'h-14 z-50' : 'h-24 rounded-xl overflow-hidden hover:bg-slate-800/60'}
      `}
    >
      <div className={`flex-1 flex justify-around items-center h-full ${isSelected ? 'px-2 gap-1' : 'px-2'}`}>
        {widget.metrics.map((m, i) => {
          const isWon = m.unit === '원';
          // Logic: Won unit gets smaller text to fit long numbers (e.g. 6,582,000)
          const valueFontSize = isCompressed
            ? (isWon ? 'text-xl' : 'text-2xl')
            : (isWon ? 'text-2xl' : 'text-4xl');

          return (
            <React.Fragment key={i}>
              <div className="flex-1 flex flex-col items-center justify-center min-w-0">
                <div className={`flex items-center gap-1.5 ${isSelected ? 'mb-0' : 'mb-1'} justify-center w-full`}>
                  <div
                    className={`${isSelected ? 'w-1.5 h-1.5' : 'w-2 h-2'} rounded-full ${getStatusColor(m.status)}`}
                    style={{ boxShadow: `0 0 8px ${getStatusShadow(m.status)}` }}
                  />
                  <span className={`${isSelected ? 'text-[11px]' : 'text-[13px]'} font-black text-slate-300 tracking-tight uppercase truncate`}>
                    {m.label}
                  </span>
                </div>
                <div className="flex items-baseline gap-1 justify-center w-full">
                  <span className={`${valueFontSize} font-black font-mono text-white tracking-tighter leading-none`}>
                    {m.value}
                  </span>
                  <span className={`${isSelected ? 'text-[8px]' : 'text-[10px]'} font-black text-slate-500 uppercase tracking-widest`}>
                    {m.unit}
                  </span>
                </div>
              </div>
              {i < widget.metrics.length - 1 && (
                <div className={`${isSelected ? 'h-6' : 'h-10'} w-[1px] bg-white/10 mx-0.5`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {isSelected && (
        <div className="absolute left-1/2 top-[-26px] -translate-x-1/2 opacity-30 group-hover:opacity-100 transition-all duration-300 pointer-events-none z-50">
          <span
            className="text-[8px] font-black text-indigo-200 bg-slate-900/90 px-3 py-1 rounded-full uppercase tracking-widest whitespace-nowrap shadow-xl"
            style={{
              border: '1px solid rgba(99, 102, 241, 0.2)',
              boxShadow: '0 0 15px rgba(0,0,0,0.5), inset 0 0 5px rgba(99, 102, 241, 0.1)'
            }}
          >
            클릭하여 대시보드로 돌아가기
          </span>
        </div>
      )}

      {/* Selection Glow */}
      {isSelected && (
        <div className="absolute inset-0 bg-white/[0.02] pointer-events-none" />
      )}
    </div>
  );
};

const getChartOption = (chartData) => {
  if (!chartData) return {};

  const baseOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(2, 6, 23, 0.95)',
      borderColor: 'rgba(99, 102, 241, 0.2)',
      textStyle: { color: '#fff', fontSize: 11 },
      padding: [10, 14],
      borderWidth: 1,
    },
    legend: {
      show: true,
      bottom: 0,
      textStyle: { color: '#94a3b8', fontSize: 11 },
      itemWidth: 10,
      itemHeight: 10
    },
    grid: { top: 40, right: 10, bottom: 45, left: 10, containLabel: true },
    xAxis: {
      type: 'category',
      data: chartData.xAxis || [],
      axisLine: { lineStyle: { color: 'rgba(255,255,255,0.1)' } },
      axisLabel: { color: '#94a3b8', fontSize: 11, interval: 0 },
    },
    yAxis: [
      {
        type: 'value',
        name: chartData.type === 'mixedActivity' ? '건수/회' : '',
        nameTextStyle: { color: '#64748b', fontSize: 10 },
        splitLine: { lineStyle: { color: 'rgba(255,255,255,0.05)', type: 'dashed' } },
        axisLabel: { color: '#94a3b8', fontSize: 11 },
      },
      ...(chartData.type === 'mixedActivity' ? [{
        type: 'value',
        name: '비율(%)',
        nameTextStyle: { color: '#64748b', fontSize: 10 },
        min: 0,
        max: 150,
        axisLabel: { formatter: '{value}%', color: '#94a3b8', fontSize: 11 },
        splitLine: { show: false }
      }] : [])
    ]
  };

  if (chartData.type === 'stackedBarQuarter') {
    baseOption.xAxis = [
      baseOption.xAxis,
      {
        type: 'category',
        data: ['', '1분기', '', '', '2분기', '', '', '3분기', '', '', '4분기', ''],
        position: 'bottom',
        offset: 15,
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: { color: '#64748b', fontSize: 10, interval: 0 }
      }
    ];
    baseOption.grid.bottom = 65;
  }

  const isRateChart = chartData.title?.includes('율') || chartData.title?.includes('Rate');

  const series = (chartData.series || []).map(s => {
    let seriesConfig = {
      name: s.name,
      data: s.data,
      smooth: true,
      symbol: 'circle',
      symbolSize: 4,
      label: {
        show: true,
        position: 'top',
        color: '#f1f5f9',
        fontSize: 10,
        fontWeight: 'bold',
        backgroundColor: 'rgba(15, 23, 42, 0.6)',
        padding: [2, 4],
        borderRadius: 4,
        formatter: (params) => {
          const name = params.seriesName || '';
          const isRate = name.includes('율') || name.includes('%') || (isRateChart && !name.includes('회') && !name.includes('건'));
          return isRate ? `${params.value}%` : params.value;
        }
      }
    };

    // Risk Level Color Mapping
    if (s.name === '상') seriesConfig.itemStyle = { color: '#ef4444' };
    else if (s.name === '중') seriesConfig.itemStyle = { color: '#f59e0b' };
    else if (s.name === '하') seriesConfig.itemStyle = { color: '#10b981' };
    // Default nice colors for other series
    else if (s.name.includes('수립') || s.name.includes('계획')) seriesConfig.itemStyle = { color: '#6366f1' };
    else if (s.name.includes('실시') || s.name.includes('실적')) seriesConfig.itemStyle = { color: '#10b981' };
    else if (s.name.includes('준수')) seriesConfig.itemStyle = { color: '#8b5cf6' };

    let seriesType = s.type || (chartData.type?.toLowerCase().includes('bar') ? 'bar' : 'line');
    seriesConfig.type = seriesType;

    if (chartData.type === 'mixedActivity' && seriesType === 'line') {
      seriesConfig.yAxisIndex = 1;
    }

    if (seriesType === 'bar') {
      seriesConfig.barMaxWidth = 20;
      if (chartData.type?.includes('stacked') || s.stack) {
        seriesConfig.stack = s.stack || 'total';
      }
    } else {
      seriesConfig.lineStyle = { width: 3 };
      seriesConfig.areaStyle = {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: (seriesConfig.itemStyle?.color || '#6366f1') + '44' },
            { offset: 1, color: 'transparent' }
          ]
        }
      };
    }

    return seriesConfig;
  });

  return { ...baseOption, series };
};

export default function SPCDashboard() {
  const { group_code, project_code } = useParams();
  const [widgets, setWidgets] = useState(INITIAL_WIDGETS);
  const [chartsData, setChartsData] = useState([]);
  const [startIndex, setStartIndex] = useState(0);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [autoSlideEnabled, setAutoSlideEnabled] = useState(false);

  // Derived charts based on selected widget
  const selectedCharts = useMemo(() => {
    if (!selectedWidget) return [];
    const widgetChartSet = chartsData.find(c => c.id === selectedWidget.id);
    return widgetChartSet ? widgetChartSet.charts : [];
  }, [selectedWidget, chartsData]);
  const chartRefs = useRef([]);

  // Fetch Dashboard Data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosPrivate.post("/dt/getDashboardData", {
          group_code,
          project_code,
        });
        if (response.data) {
          setWidgets(response.data.widgets || []);
          setChartsData(response.data.charts || []);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setWidgets([]);
        setChartsData([]);
      }
    };
    fetchData();
    // Chart telemetry polling removed as per user request
    // const interval = setInterval(fetchData, 5000);
    // return () => clearInterval(interval);
  }, [group_code, project_code]);

  // Auto-slide logic for widget mode (10s interval)
  useEffect(() => {
    let intervalId;
    if (!selectedWidget && !isHovered && widgets.length > 3 && autoSlideEnabled) {
      intervalId = setInterval(() => {
        setStartIndex((prev) => (prev + 1) % widgets.length);
      }, 10000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [selectedWidget, widgets.length, isHovered, autoSlideEnabled]);

  const nextWidgets = () => setStartIndex((prev) => (prev + 1) % widgets.length);
  const prevWidgets = () => setStartIndex((prev) => (prev - 1 + widgets.length) % widgets.length);

  const visibleWidgets = useMemo(() => {
    if (widgets.length === 0) return [];
    return [
      widgets[startIndex % widgets.length],
      widgets[(startIndex + 1) % widgets.length],
      widgets[(startIndex + 2) % widgets.length]
    ].filter(Boolean);
  }, [startIndex, widgets]);

  const handleWidgetClick = (widget) => {
    setSelectedWidget(selectedWidget?.id === widget.id ? null : widget);
  };

  const handleNextSelected = (e) => {
    e.stopPropagation();
    if (!selectedWidget) return;
    const currentIdx = widgets.findIndex(w => w.id === selectedWidget.id);
    const nextIdx = (currentIdx + 1) % widgets.length;
    setSelectedWidget(widgets[nextIdx]);
  };

  const handlePrevSelected = (e) => {
    e.stopPropagation();
    if (!selectedWidget) return;
    const currentIdx = widgets.findIndex(w => w.id === selectedWidget.id);
    const prevIdx = (currentIdx - 1 + widgets.length) % widgets.length;
    setSelectedWidget(widgets[prevIdx]);
  };

  // Resize charts AND trigger render check when switching to graph mode
  useEffect(() => {
    if (selectedWidget) {
      // Use requestAnimationFrame to sync with the next paint
      requestAnimationFrame(() => {
        // Double safety for CSS transition delay
        const timer = setTimeout(() => {
          chartRefs.current.forEach((chart) => chart?.resize());
        }, 350); // Slightly longer than the 300ms CSS transition
        return () => clearTimeout(timer);
      });
    }
  }, [selectedWidget]);

  // Window Resize Listener
  useEffect(() => {
    const handleResize = () => chartRefs.current.forEach((chart) => chart?.resize());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="absolute bottom-0 left-0 right-0 z-40 pointer-events-none font-['Pretendard']">

      {/* 
        PERFORMANCE OPTIMIZATION:
        Uses pure CSS 'transform' transitions instead of Framer Motion (JS).
        'will-change-transform' promotes these layers to the GPU Compositor thread,
        bypassing the Main Thread which is heavily used by Unity WebGL.
      */}
      <div className="relative w-full h-[500px]"> {/* Container height to manage absolute positions */}

        {/* 1. CAROUSEL LAYER */}
        <div
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onContextMenu={(e) => e.preventDefault()}
          className={`
            absolute bottom-0 left-0 w-full pb-10
            transition-all duration-300 ease-out will-change-transform
            flex flex-col items-center gap-4 justify-center pointer-events-auto
            ${selectedWidget
              ? 'opacity-0 translate-y-20 pointer-events-none delay-0'
              : 'opacity-100 translate-y-0 delay-100'
            }
          `}
        >
          {/* Auto Slide Toggle */}
          <div className="w-full max-w-[98%] flex justify-end px-16">
            <div
              onClick={() => setAutoSlideEnabled(!autoSlideEnabled)}
              className="group flex items-center gap-3 px-4 py-2 rounded-xl bg-slate-900/40 backdrop-blur-md border border-white/5 cursor-pointer hover:bg-slate-800/60 transition-all active:scale-95"
            >
              <span className={`text-[10px] font-black uppercase tracking-widest ${autoSlideEnabled ? 'text-indigo-400' : 'text-slate-500'}`}>
                Auto Slide {autoSlideEnabled ? 'On' : 'Off'}
              </span>
              <div className={`w-8 h-4 rounded-full p-1 transition-colors duration-300 ${autoSlideEnabled ? 'bg-indigo-500/30' : 'bg-white/5'}`}>
                <div className={`w-2 h-2 rounded-full transition-all duration-300 ${autoSlideEnabled ? 'bg-indigo-400 translate-x-4 shadow-[0_0_8px_rgba(129,140,248,0.6)]' : 'bg-slate-600 translate-x-0'}`} />
              </div>
            </div>
          </div>

          <div className="w-full max-w-[98%] flex items-center gap-4">
            <button
              onClick={prevWidgets}
              className="w-12 h-12 flex items-center justify-center rounded-full text-white/40 hover:text-white transition-all hover:bg-white/10"
            >
              <NavigateBeforeIcon sx={{ fontSize: 40 }} />
            </button>

            <div className="flex-1 grid grid-cols-3 gap-4">
              {visibleWidgets.map((widget) => (
                <div key={widget.id} className="w-full">
                  <WidgetCard
                    widget={widget}
                    onClick={handleWidgetClick}
                  />
                </div>
              ))}
            </div>

            <button
              onClick={nextWidgets}
              className="w-12 h-12 flex items-center justify-center rounded-full text-white/40 hover:text-white transition-all hover:bg-white/10"
            >
              <NavigateNextIcon sx={{ fontSize: 40 }} />
            </button>
          </div>
        </div>

        {/* 2. GRAPH MODE LAYERS */}

        {/* Selected Widget Floating */}
        <div
          onContextMenu={(e) => e.preventDefault()}
          className={`
            absolute bottom-[320px] left-0 right-0 z-50 flex items-center justify-center gap-8 pointer-events-auto
            transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] will-change-transform
            ${selectedWidget
              ? 'opacity-100 translate-y-0 delay-100'
              : 'opacity-0 translate-y-10 pointer-events-none delay-0'
            }
          `}
        >
          <button
            onClick={handlePrevSelected}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-900/50 text-white/50 hover:text-white hover:bg-indigo-600 transition-colors"
          >
            <NavigateBeforeIcon sx={{ fontSize: 32 }} />
          </button>

          <div className="w-[640px] max-w-[85vw]">
            {selectedWidget && (
              <WidgetCard
                widget={selectedWidget}
                isSelected={true}
                onClick={() => setSelectedWidget(null)}
              />
            )}
          </div>

          <button
            onClick={handleNextSelected}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-800/40 text-white/50 hover:text-white hover:bg-indigo-600 transition-colors"
          >
            <NavigateNextIcon sx={{ fontSize: 32 }} />
          </button>
        </div>

        {/* Graph Bar */}
        <div
          onContextMenu={(e) => e.preventDefault()}
          className={`
            absolute bottom-0 left-0 w-full h-[320px] 
            bg-[#020617]/90 ring-t ring-white/5 shadow-[0_-20px_50px_rgba(0,0,0,0.5)]
            flex items-center justify-center pointer-events-auto
            transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] will-change-transform
            ${selectedWidget
              ? 'translate-y-0 delay-0'
              : 'translate-y-full delay-100'
            }
          `}
        >
          <div className="w-full h-full px-2 py-2 grid grid-cols-4 gap-2">
            {[0, 1, 2, 3].map((idx) => (
              <div
                key={idx}
                className="bg-white/[0.02] ring-1 ring-inset ring-white/5 rounded-xl p-2 flex flex-col hover:bg-white/[0.04] transition-colors h-full"
              >
                <div className="flex justify-between items-center mb-1.5 px-1">
                  <span className="text-[12px] font-black text-slate-100 uppercase tracking-widest truncate max-w-[80%]">
                    {selectedCharts[idx]?.title || `DATA STREAM ${idx + 1}`}
                  </span>
                  <div className="w-2 h-2 rounded-full bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.5)]" />
                </div>
                <div className="flex-1 w-full relative">
                  {selectedWidget && selectedCharts[idx] && (
                    <ReactECharts
                      onChartReady={(echartsInstance) => {
                        chartRefs.current[idx] = echartsInstance;
                      }}
                      notMerge={true}
                      option={getChartOption(selectedCharts[idx])}
                      style={{ height: '100%', width: '100%', position: 'absolute', top: 0, left: 0 }}
                      opts={{ renderer: 'svg' }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
